import React, { useEffect, useState } from 'react';
import api from '../api';
import {
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  Surface,
  SurfaceBody,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface RewardRules {
  bitsThreshold: string;
  claimsPerSub: string;
  claimsPerGift: string;
  claimsPerCheer: string;
  claimsPerChest: string;
  streamlootsAlertId: string;
}

interface EventSubStatus {
  subscriptionsActive?: boolean;
  lastEnsureError?: string;
  acceptingIrc?: boolean;
  authorized?: boolean;
  subscriptions?: { type: string; status: string }[];
  authorize?: { url: string };
  rules?: RewardRules;
  streamloots?: {
    listening?: boolean;
    alertIdSet?: boolean;
    lastError?: string;
  };
}

const emptyRules: RewardRules = {
  bitsThreshold: '100',
  claimsPerSub: '1',
  claimsPerGift: '1',
  claimsPerCheer: '1',
  claimsPerChest: '1',
  streamlootsAlertId: '',
};

function asRules(rules?: Partial<Record<keyof RewardRules, string | number>>): RewardRules {
  const next = { ...emptyRules };
  (Object.keys(emptyRules) as (keyof RewardRules)[]).forEach((key) => {
    if (rules && rules[key] !== undefined && rules[key] !== null) next[key] = String(rules[key]);
  });
  return next;
}

const OracleConfigPage: React.FC = () => {
  const [status, setStatus] = useState<EventSubStatus | null>(null);
  const [rules, setRules] = useState<RewardRules>(emptyRules);
  const [chestName, setChestName] = useState('');
  const [chestQty, setChestQty] = useState('1');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    const { data } = await api.get<EventSubStatus>('/twitch/eventsub/status');
    setStatus(data);
    if (data.rules) setRules(asRules(data.rules));
  };

  useEffect(() => {
    load().catch((e: any) => {
      setError(e?.response?.data?.message || e.message || 'No se pudo cargar la configuración');
    });
  }, []);

  const setNumber = (key: keyof RewardRules, value: string) => {
    setRules((prev) => ({ ...prev, [key]: value.replace(/[^\d]/g, '') }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/twitch/eventsub/rules', {
        bitsThreshold: Number(rules.bitsThreshold) || 100,
        claimsPerSub: Number(rules.claimsPerSub) || 0,
        claimsPerGift: Number(rules.claimsPerGift) || 0,
        claimsPerCheer: Number(rules.claimsPerCheer) || 0,
        claimsPerChest: Number(rules.claimsPerChest) || 0,
        streamlootsAlertId: rules.streamlootsAlertId.trim(),
      });
      setRules(asRules(data.rules));
      await load();
      setToast('Cartas por evento guardadas.');
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const ensureEventSub = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/twitch/eventsub/ensure');
      await load();
      setToast(
        data.subscriptionsActive
          ? 'EventSub activo: subs y bits acreditan oráculos.'
          : data.needsAuth
            ? 'Hay que autorizar Twitch (cuenta de Eugenia).'
            : data.error || 'EventSub parcial.',
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error EventSub');
    } finally {
      setBusy(false);
    }
  };

  const creditChest = async () => {
    const username = chestName.trim();
    const quantity = Math.max(1, parseInt(chestQty, 10) || 1);
    if (!username) {
      setError('Poné el nombre de Twitch de quien compró.');
      return;
    }
    setCrediting(true);
    setError('');
    try {
      const { data } = await api.post('/twitch/eventsub/chests', { username, quantity });
      setChestName('');
      setChestQty('1');
      setToast(`+${data.granted} carta(s) para ${data.user}.`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo acreditar el cofre');
    } finally {
      setCrediting(false);
    }
  };

  const streamlootsLabel = () => {
    if (!rules.streamlootsAlertId) return 'sin ID de alertas';
    if (status?.streamloots?.listening) return 'escuchando compras';
    if (status?.streamloots?.lastError) return status.streamloots.lastError;
    return 'conectando…';
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Configuración Oráculos</Title>
          <Subtitle>
            Cuántas cartas suma cada evento. Los valores de base son los de ahora: 1 por sub, 1 por sub
            regalada para quien regala, 1 por cheer de 100 bits o más, y 1 por cada cofre de Streamloots.
          </Subtitle>
        </PageHeaderMain>
      </PageHeader>

      {toast && <Toast onClick={() => setToast('')}>{toast}</Toast>}
      {error && (
        <Toast style={{ borderColor: 'crimson' }} onClick={() => setError('')}>
          {error}
        </Toast>
      )}

      <Surface>
        <SurfaceBody>
          <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Cartas por evento
          </Muted>
          <FormGrid style={{ marginTop: '0.85rem' }}>
            <FormRow>
              <Label>Sub o resub</Label>
              <Input
                type="number"
                min={0}
                value={rules.claimsPerSub}
                onChange={(e) => setNumber('claimsPerSub', e.target.value)}
              />
              <Muted>Cartas para quien se suscribe.</Muted>
            </FormRow>
            <FormRow>
              <Label>Sub regalada</Label>
              <Input
                type="number"
                min={0}
                value={rules.claimsPerGift}
                onChange={(e) => setNumber('claimsPerGift', e.target.value)}
              />
              <Muted>Por cada sub regalada. Siempre para quien regala.</Muted>
            </FormRow>
            <FormRow>
              <Label>Bits mínimos del cheer</Label>
              <Input
                type="number"
                min={1}
                value={rules.bitsThreshold}
                onChange={(e) => setNumber('bitsThreshold', e.target.value)}
              />
              <Muted>Por debajo de esto no suma carta.</Muted>
            </FormRow>
            <FormRow>
              <Label>Cheer que llega al mínimo</Label>
              <Input
                type="number"
                min={0}
                value={rules.claimsPerCheer}
                onChange={(e) => setNumber('claimsPerCheer', e.target.value)}
              />
              <Muted>Una donación, no una carta cada 100 bits.</Muted>
            </FormRow>
            <FormRow>
              <Label>Cofre de Streamloots</Label>
              <Input
                type="number"
                min={0}
                value={rules.claimsPerChest}
                onChange={(e) => setNumber('claimsPerChest', e.target.value)}
              />
              <Muted>Se multiplica por los cofres de esa compra. 3 cofres = 3 veces este número.</Muted>
            </FormRow>
            <FormRow>
              <Label>ID de alertas de Streamloots</Label>
              <Input
                value={rules.streamlootsAlertId}
                placeholder="el código al final de la URL de alertas"
                onChange={(e) => setRules((prev) => ({ ...prev, streamlootsAlertId: e.target.value }))}
              />
              <Muted>
                En Streamloots → Alertas, copiá la URL (widgets.streamloots.com/alerts/…). Estado:{' '}
                {streamlootsLabel()}.
              </Muted>
            </FormRow>
          </FormGrid>
          <Toolbar style={{ marginTop: '0.9rem' }}>
            <Button type="button" disabled={saving} onClick={save}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </Toolbar>
        </SurfaceBody>
      </Surface>

      <Surface>
        <SurfaceBody>
          <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Acreditar cofres a mano
          </Muted>
          <Muted style={{ marginTop: '0.4rem' }}>
            Si la compra no entró sola, poné el nick de quien pagó y cuántos cofres compró. También se
            acredita al cargar un cofre en Extensible, si escribís el nombre de Twitch.
          </Muted>
          <FormGrid style={{ marginTop: '0.75rem' }}>
            <FormRow>
              <Label>Nombre de Twitch</Label>
              <Input value={chestName} onChange={(e) => setChestName(e.target.value)} placeholder="quien compró" />
            </FormRow>
            <FormRow>
              <Label>Cofres</Label>
              <Input type="number" min={1} value={chestQty} onChange={(e) => setChestQty(e.target.value)} />
            </FormRow>
          </FormGrid>
          <Toolbar style={{ marginTop: '0.75rem' }}>
            <Button type="button" disabled={crediting} onClick={creditChest}>
              {crediting ? 'Acreditando…' : 'Acreditar cartas'}
            </Button>
          </Toolbar>
        </SurfaceBody>
      </Surface>

      <Surface>
        <SurfaceBody>
          <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Subs / bits → oráculos
          </Muted>
          <div style={{ marginTop: '0.45rem', color: '#e8dff0', fontSize: '0.86rem', lineHeight: 1.45 }}>
            EventSub:{' '}
            {status?.subscriptionsActive ? (
              <span style={{ color: '#9dffb0' }}>activo</span>
            ) : (
              <span style={{ color: '#ffb4d9' }}>pendiente</span>
            )}
            {status?.acceptingIrc ? ' · IRC acreditando' : ''}
            <br />
            Autorización de Eugenia:{' '}
            {status?.authorized ? (
              <span style={{ color: '#9dffb0' }}>lista</span>
            ) : (
              <span style={{ color: '#ffb4d9' }}>falta</span>
            )}
            {status?.subscriptions?.length ? (
              <>
                <br />
                {status.subscriptions.map((sub) => (
                  <span key={sub.type} style={{ marginRight: '0.7rem', fontSize: '0.8rem' }}>
                    {sub.type}:{' '}
                    <span style={{ color: sub.status === 'enabled' ? '#9dffb0' : '#ffb4d9' }}>{sub.status}</span>
                  </span>
                ))}
              </>
            ) : null}
            {status?.lastEnsureError ? (
              <>
                <br />
                <span style={{ opacity: 0.75, fontSize: '0.8rem' }}>{status.lastEnsureError}</span>
              </>
            ) : null}
          </div>
          <Toolbar style={{ marginTop: '0.65rem', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button type="button" $variant="ghost" $size="sm" disabled={busy} onClick={ensureEventSub}>
              {busy ? '…' : 'Reintentar EventSub'}
            </Button>
            {status?.authorize?.url && (
              <Button
                type="button"
                $size="sm"
                onClick={() => window.open(status.authorize!.url, '_blank', 'noopener,noreferrer')}
              >
                Autorizar Twitch (Eugenia)
              </Button>
            )}
          </Toolbar>
        </SurfaceBody>
      </Surface>
    </Page>
  );
};

export default OracleConfigPage;
